import WaterfallTabHeader from './WaterfallTabHeader'

export default function AllocationMethodology() {
  return (
    <div className="w-full">
      <WaterfallTabHeader
        title="Methodology"
        description="Why income allocations are computed the way they are."
      />

      <div className="max-w-3xl space-y-8 text-sm text-gray-700">
        <section>
          <h3 className="font-semibold text-gray-900">Two ledgers</h3>
          <p className="mt-2">
            Cash distributions and income allocations are strictly separate. The Cash distributions
            mode moves real money through the four-tier Operations Waterfall. Income allocation is
            a book exercise: it assigns each period&apos;s net income or loss to member capital
            accounts and moves no cash. Nothing logged on the income side ever changes a cash-side
            number.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900">Targeted capital account method</h3>
          <p className="mt-2">
            Each period: (1) roll every member&apos;s capital forward — beginning balance +
            contributions ± capital transfers − cash distributions; (2) compute each member&apos;s
            target capital: what they would receive if the company were liquidated at book value at
            period end and the proceeds paid out through the waterfall; (3) allocate net income/loss
            so ending capital equals target. Allocations always sum exactly to entered net income
            (rounding residual ≤ $0.02 plugs to JR). This is the standard method for LLCs taxed as
            partnerships whose agreements distribute through a waterfall: allocations follow where the
            dollars would actually go.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900">Why the Liquidity Event Waterfall</h3>
          <p className="mt-2">
            Targets are computed under the LLCA&apos;s Liquidity Event Waterfall — not the day-to-day
            Operations Waterfall — because capital accounts are ultimately settled in a wind-up, and
            the LLCA directs wind-up proceeds through this ordering:
          </p>
          <ol className="mt-2 list-decimal space-y-2 pl-5">
            <li>
              <strong>HP Threshold</strong> — 100% to Class A in HP Threshold Proportions (KB
              10.7876%, JR remainder) until the Unreturned HP Threshold is repaid. The claim includes
              preference accrued since the last quarterly compounding (the economic balance). Class A
              is 100% JR before KB&apos;s Class A grant date (2026-01-02).
            </li>
            <li>
              <strong>RO Threshold</strong> — 100% to JR until the fixed $6,491,000.00 RO Threshold is
              repaid. No return accrues on it, and only liquidity-event distributions to JR reduce it
              (none have occurred).
            </li>
            <li>
              <strong>Thereafter</strong> — pro rata by Class A + Class C granted units (Class C
              participates only in liquidity proceeds; entitlement basis, no vesting reduction).
            </li>
          </ol>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900">The River Oaks contribution</h3>
          <p className="mt-2">
            On 2025-04-01 JR contributed the River Oaks store — $5,856,000 buildings &amp;
            leasehold improvements, $144,000 furniture &amp; fixtures, $491,000 goodwill,
            $6,491,000.00 total — in exchange for the fixed 1× RO Threshold. The contributed
            assets&apos; book depreciation and amortization get no special tagging. While total book
            equity sits at or below the two thresholds, JR is the marginal claimant in a hypothetical
            wind-up, so depreciation on those assets — and losses generally — allocates to her
            automatically. Once equity grows past HP + RO, her fixed preference is fully covered and
            book declines shift pro rata to the residual owners, which is equally correct: at that
            point she no longer bears them. The method self-corrects; no asset-to-member carve-outs
            are needed or used.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900">Keller and Brittany's capital transfer</h3>
          <p className="mt-2">
            On 2026-01-01 KB purchased 10.7876% of the Class A position from JR personally (not from
            the Company) for $400,000. A buyer of a membership interest steps into the seller&apos;s
            capital account, so BookKeeper records a member-to-member capital transfer (JR −$400,000,
            KB +$400,000) on the Settings tab. The amount equals the wind-up value of the
            transferred interest at the sale date — 10.7876% × the unreturned HP Threshold of
            $3,707,950.47 = $400,000.00 exactly, matching the purchase price by construction. Without
            this transfer the engine would steer income allocations to KB (away from JR) to fill his
            HP-tier claim, misstating both members&apos; capital.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900">Tier 1 is not a guaranteed payment</h3>
          <p className="mt-2">
            The $150,000/yr day-counted amounts to LVW and TR are a cash-waterfall priority, not
            §707(c) guaranteed payments. Net income is entered before any member payments, and under
            the targeted method LVW/TR receive income allocations that naturally offset the Tier 1
            cash they were paid.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900">Losses and negative capital</h3>
          <p className="mt-2">
            Loss periods produce negative allocations — the targeted formula is the reverse
            waterfall. Capital accounts may go negative; the UI flags them but does not block. That
            is a fact for the CPA&apos;s basis analysis, not an error.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900">Book vs. tax</h3>
          <p className="mt-2">
            These are book capital accounts. Tax allocations for the contributed River Oaks assets
            (§704(c)) are handled by the CPA on the K-1s and will legitimately differ from these
            entries.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900">Rounding</h3>
          <p className="mt-2">
            Half-away-from-zero to cents (
            <code className="rounded bg-gray-100 px-1 text-xs">R2</code>
            ). In Class A splits KB is the rounded leg and JR the residual; the thereafter-tier and
            the final allocation plug rounding residuals to JR.
          </p>
        </section>
      </div>
    </div>
  )
}
